import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  CButton, CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CFormTextarea, CSpinner,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilBan, cilCheck, cilList, cilPencil, cilTrash, cilSearch } from '@coreui/icons'

import { wtApiCall } from '../../helpers/commons/ApiWrapper'
import {
  lzNotifyErrorMessage, lzNotifyErrorMessageFromResult, lzNotifySuccessMessage,
} from '../../helpers/commons/notifications'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { wtFormatMoneyRoundData } from 'src/helpers/commons/utils'
import { PropertyTypeOptions } from 'src/helpers/commons/dropdownOptions'
import { APP_BASE_URL } from '../../config/Request'
import BaseListing from 'src/BaseListings'
import Form from 'src/views/Properties/Form'
import './PendingProperties.css'

const PAGE_SIZE = 10
const DOT = ' \u00B7 '

export const RejectReasons = [
  'Missing or poor photos',
  'Wrong or missing details',
  'Price unclear',
  'Duplicate listing',
  'Description needs work',
]

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
const optValue = (o) => (typeof o === 'string' ? o : o.value)
const optLabel = (o) => (typeof o === 'string' ? o : o.label || o.value)
const bedsText = (b) =>
  b === null || b === undefined || b === '' ? '—' : Number(b) === 0 ? 'Studio' : b
const isChanges = (r) => Number(r.approval_status) === 2
const isPendingRow = (r) => Number(r.approval_status) === 0
const areaPending = (r) => Number(r.area_requested) === 1

export default function PendingProperties() {
  const Base = new BaseListing()
  const navigate = useNavigate()
  const location = useLocation()

  const user = wtGetFromLocalStorage('authUser')
  const roleName = user?.role_name || user?.user?.role_name
  const myId = Number(user?.id ?? user?.user?.id)
  const isBackOffice = ['Super Admin', 'Manager', 'Staff'].includes(roleName)
  const isStaff = roleName === 'Staff'
  const canAdd = !isBackOffice && Base.hasRights('Add Property')

  const [tab, setTab] = useState(isBackOffice ? 'review' : 'all')
  const [keyword, setKeyword] = useState('')
  const [propertyType, setPropertyType] = useState('')
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState({ total: 0, from: 0, to: 0, last_page: 1 })
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)

  const [editId, setEditId] = useState(0)
  const [isAdding, setIsAdding] = useState(false)

  const [approvingId, setApprovingId] = useState(0)
  const [rejectId, setRejectId] = useState(0)
  const [rejectPicks, setRejectPicks] = useState([])
  const [rejectNote, setRejectNote] = useState('')
  const [rejecting, setRejecting] = useState(false)

    // Open the form automatically: ?add=1 (dashboard) or ?edit=ID (view page)
  const [backTo, setBackTo] = useState('')
  useEffect(() => {
    const q = new URLSearchParams(location.search)
    const editParam = Number(q.get('edit') || 0)
    if (q.get('add') === '1') {
      setEditId(0)
      setIsAdding(true)
      navigate('/pending-properties', { replace: true })
    } else if (editParam > 0 && !isBackOffice) {
      setEditId(editParam)
      setIsAdding(true)
      setBackTo('/property-profile/' + editParam)
      navigate('/pending-properties', { replace: true })
    }
  }, [location.search])


  const statusFor = (t) => (t === 'review' ? [0] : t === 'changes' ? [2] : [0, 2])

  const getData = (p = page) => {
    setIsLoading(true)
    wtApiCall(
      'property',
      {
        _page_index: p,
        _page_size: PAGE_SIZE,
        keyword: keyword.trim(),
        property_type: propertyType,
        approval_status: statusFor(tab),
      },
      'get',
      (response) => {
        const d = response.data || {}
        setRows(d.data || [])
        setMeta({ total: d.total || 0, from: d.from || 0, to: d.to || 0, last_page: d.last_page || 1 })
        setIsLoading(false)
      },
      (error) => {
        setIsLoading(false)
        lzNotifyErrorMessageFromResult(error)
      },
    )
  }

  // Reload when tab or filters change (short pause while typing)
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1)
      getData(1)
    }, 300)
    return () => clearTimeout(t)
  }, [tab, keyword, propertyType])

  const goPage = (p) => {
    setPage(p)
    getData(p)
  }

  const onEdit = (id) => {
    setEditId(id)
    setIsAdding(true)
  }

  const onCloseForm = () => {
    setIsAdding(false)
    setEditId(0)
    if (backTo) {
      const to = backTo
      setBackTo('')
      navigate(to)
      return
    }
    getData()
  }


  const approve = (r) => {
    if (areaPending(r)) {
      lzNotifyErrorMessage('Please resolve the requested area before approving.')
      return
    }
    if (!window.confirm(`Approve ${r.ref}? It will become visible to all KLS agents.`)) return
    setApprovingId(r.id)
    wtApiCall(
      'property/' + r.id,
      { action: 'approve' },
      'PUT',
      (response) => {
        setApprovingId(0)
        lzNotifySuccessMessage(response.data)
        getData()
      },
      (error) => {
        setApprovingId(0)
        lzNotifyErrorMessageFromResult(error)
      },
    )
  }

  const rejectReasonText = () => {
    const note = rejectNote.trim()
    return [rejectPicks.join(', '), note].filter(Boolean).join(' — ')
  }

  const openReject = (id) => {
    setRejectId(id)
    setRejectPicks([])
    setRejectNote('')
  }

  const submitReject = () => {
    const reason = rejectReasonText()
    if (!reason) return
    setRejecting(true)
    wtApiCall(
      'property/' + rejectId,
      { action: 'reject', rejection_reason: reason },
      'PUT',
      (response) => {
        setRejecting(false)
        setRejectId(0)
        lzNotifySuccessMessage(response.data)
        getData()
      },
      (error) => {
        setRejecting(false)
        lzNotifyErrorMessageFromResult(error)
      },
    )
  }

  // ---------- small display pieces ----------
  const agentName = (r) =>
    r.created_by ? `${r.created_by.first_name || ''} ${r.created_by.last_name || ''}`.trim() : '—'
  const locText = (r) => [r.neighborhood_one, r.city_county].filter(Boolean).join(', ')
  const listingTitle = (r) => [r.property_type || 'Property', r.status].filter(Boolean).join(DOT)
  const priceText = (r) => wtFormatMoneyRoundData(r.original_price, r.currency || 'KES')
  const dateInfo = (r) =>
    isChanges(r)
      ? { label: 'Returned', value: fmtDate(r.rejected_at || r.updated_at || r.created_at) }
      : { label: 'Submitted', value: fmtDate(r.created_at) }

  const Thumb = ({ r }) =>
    r.featured_image ? (
        <img className="pp-thumb" src={APP_BASE_URL + r.featured_image} alt="" loading="lazy" />
    ) : (
      <div className="pp-noimg">No image</div>
    )

  const StatusBadge = ({ r }) => {
    if (isChanges(r)) return <span className="pp-badge pp-badge-changes">Changes required</span>
    if (Number(r.is_relisting) === 1) return <span className="pp-badge pp-badge-relist">Re-listing</span>
    return <span className="pp-badge pp-badge-review">Under review</span>
  }

  const Notes = ({ r }) => {
    return (

      <>
        {isChanges(r) && r.rejection_reason && (
          <div className="pp-note pp-note-red">
            <strong>Feedback from KLS:</strong> {r.rejection_reason}
          </div>
        )}

        {areaPending(r) && (
          <div className="pp-note pp-note-gold">
            <strong>New area requested:</strong> {r.neighborhood_one || '—'}
          </div>
        )}
      </>
    )
  }

  const Actions = ({ r }) => {
    const isOwner = Number(r.created_by_id) === myId
    const canEdit = Base.hasRights('Edit Property') && isOwner
    const canDelete = isBackOffice ? !isStaff : Base.hasRights('Delete Property') && isOwner
    return (
      <div className="pp-actions">
        <button className="pp-btn" onClick={() => navigate(`/property-profile/${r.id}`)}>
          <CIcon icon={cilList} /> View
        </button>
        {canEdit && (
          <button className="pp-btn" onClick={() => onEdit(r.id)}>
            <CIcon icon={cilPencil} /> Edit
          </button>
        )}
        {isBackOffice && isPendingRow(r) && (
          <>
            <button
              className="pp-btn pp-btn-approve"
              disabled={approvingId === r.id}
              title={areaPending(r) ? 'Resolve the requested area first' : 'Approve listing'}
              onClick={() => approve(r)}
            >
              <CIcon icon={cilCheck} /> {approvingId === r.id ? 'Approving...' : 'Approve'}
            </button>
            <button className="pp-btn pp-btn-reject" onClick={() => openReject(r.id)}>
              <CIcon icon={cilBan} /> Request changes
            </button>
          </>
        )}
        {canDelete && (
          <button
            className="pp-btn pp-btn-delete"
            onClick={() => Base.deleteData('property', r.id, () => getData())}
          >
            <CIcon icon={cilTrash} /> Delete
          </button>
        )}
      </div>
    )
  }

  // ---------- texts ----------
  const tabs = isBackOffice
    ? [
        { key: 'review', label: 'Under review' },
        { key: 'changes', label: 'Changes required' },
      ]
    : [
        { key: 'all', label: 'All' },
        { key: 'review', label: 'Under review' },
        { key: 'changes', label: 'Changes required' },
      ]

  const title = isBackOffice ? 'Listings for review' : 'My listings under review'
  const subtitle = isBackOffice
    ? tab === 'review'
      ? 'New and re-listed properties waiting for approval.'
      : 'Listings returned to agents. They move back to Under review once the agent resubmits.'
    : 'Listings waiting for KLS approval, and listings that need changes before they can go live.'

  const emptyText = isBackOffice
    ? tab === 'review'
      ? 'No listings are waiting for review.'
      : 'No listings are waiting on agent changes.'
    : tab === 'changes'
      ? 'None of your listings need changes.'
      : 'You have no listings under review.'

  const hasFilters = keyword || propertyType

  if (isAdding) {
    return <Form onClose={onCloseForm} editId={editId} viewOnly={false} />
  }

  return (
    <div className="pp-page">
      <div className="pp-head">
        <div>
          <h4 className="pp-title">{title}</h4>
          <div className="pp-sub">{subtitle}</div>
        </div>
        {canAdd && (
          <button className="pp-add" onClick={() => { setEditId(0); setIsAdding(true) }}>
            + Add property
          </button>
        )}
      </div>

      <div className="pp-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={'pp-tab' + (tab === t.key ? ' active' : '')}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="pp-filters">
        <div className="pp-search">
          <CIcon icon={cilSearch} className="icon" />
          <input
            type="text"
            placeholder="Search by ref (e.g. KLS-0007), name or area"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>
        <select className="pp-select" value={propertyType} onChange={(e) => setPropertyType(e.target.value)}>
          <option value="">All property types</option>
          {(PropertyTypeOptions || []).map((o) => (
            <option key={optValue(o)} value={optValue(o)}>
              {optLabel(o)}
            </option>
          ))}
        </select>
        {hasFilters && (
          <button className="pp-clear" onClick={() => { setKeyword(''); setPropertyType('') }}>
            Clear filters
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="pp-loading">
          <CSpinner color="success" />
        </div>
      ) : rows.length === 0 ? (
        <div className="pp-empty">{hasFilters ? 'No listings match your filters.' : emptyText}</div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="pp-table-wrap">
            <table className="pp-table">
              <thead>
                <tr>
                  <th>Ref</th>
                  <th>Listing</th>
                  <th>Beds / Baths</th>
                  <th>Price</th>
                  {isBackOffice && <th>Listing agent</th>}
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const d = dateInfo(r)
                  return (
                    <tr key={r.id}>
                      <td className="pp-ref">{r.ref}</td>
                      <td>
                        <div className="pp-listing">
                          <Thumb r={r} />
                          <div>
                            <div className="pp-name">{listingTitle(r)}</div>
                            <div className="pp-loc">{locText(r) || '—'}</div>
                            <Notes r={r} />
                          </div>
                        </div>
                      </td>
                      <td>
                        {r.property_type === 'Land' ? '—' : `${bedsText(r.bed_rooms)} / ${r.rest_rooms ?? '—'}`}
                      </td>
                      <td className="pp-price">{priceText(r)}</td>
                      {isBackOffice && <td>{agentName(r)}</td>}
                      <td>
                        <div className="pp-muted">{d.label}</div>
                        <div style={{ whiteSpace: 'nowrap' }}>{d.value}</div>
                      </td>
                      <td>
                        <StatusBadge r={r} />
                      </td>
                      <td>
                        <Actions r={r} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Phone cards */}
          <div className="pp-cards">
            {rows.map((r) => {
              const d = dateInfo(r)
              return (
                <div className="pp-card" key={r.id}>
                  <div className="pp-card-top">
                    <Thumb r={r} />
                    <div>
                      <div className="pp-ref">{r.ref}</div>
                      <div className="pp-name">{listingTitle(r)}</div>
                      <div className="pp-loc">{locText(r) || '—'}</div>
                      <div className="pp-price">{priceText(r)}</div>
                    </div>
                  </div>
                  <div className="pp-card-meta">
                    <StatusBadge r={r} />
                    {r.property_type !== 'Land' && (
                      <span>{bedsText(r.bed_rooms)} bed{DOT}{r.rest_rooms ?? '—'} bath</span>
                    )}
                    {isBackOffice && <span>{agentName(r)}</span>}
                    <span>{d.label} {d.value}</span>
                  </div>
                  <Notes r={r} />
                  <Actions r={r} />
                </div>
              )
            })}
          </div>

          <div className="pp-pager">
            <span>
              Showing {meta.from}–{meta.to} of {meta.total}
            </span>
            {meta.last_page > 1 && (
              <div className="pp-pager-btns">
                <button className="pp-btn" disabled={page <= 1} onClick={() => goPage(page - 1)}>
                  Previous
                </button>
                <span>
                  Page {page} of {meta.last_page}
                </span>
                <button className="pp-btn" disabled={page >= meta.last_page} onClick={() => goPage(page + 1)}>
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      <CModal visible={rejectId > 0} onClose={() => setRejectId(0)} alignment="center">
        <CModalHeader>
          <CModalTitle>Request changes</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <div className="mb-2">What needs to change?</div>
          <div className="d-flex flex-wrap gap-2 mb-3">
            {RejectReasons.map((r) => {
              const on = rejectPicks.includes(r)
              return (
                <button
                  type="button"
                  key={r}
                  onClick={() => setRejectPicks(on ? rejectPicks.filter((x) => x !== r) : [...rejectPicks, r])}
                  style={{
                    border: `1px solid ${on ? '#1F4D3A' : '#c9ced6'}`,
                    background: on ? '#1F4D3A' : '#fff',
                    color: on ? '#fff' : '#333',
                    borderRadius: 20,
                    padding: '5px 14px',
                    fontSize: 14,
                  }}
                >
                  {on && <i className="fa fa-check" />} {r}
                </button>
              )
            })}
          </div>
          <CFormTextarea
            rows={3}
            placeholder="Add a note for the agent (optional if you selected a reason above)"
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
          />
          <div className="small text-medium-emphasis mt-2">
            The agent will see this feedback on the listing.
          </div>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setRejectId(0)}>
            Cancel
          </CButton>
          <CButton
            color="danger"
            className="text-white"
            disabled={!rejectReasonText() || rejecting}
            onClick={submitReject}
          >
            {rejecting ? 'Sending...' : 'Send to agent'}
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  )
}